/**
 * LegalModal — reusable modal for site policies, About Us, and support docs.
 * Triggered by footer links. Content is rendered inline (no external fetch needed).
 * Uses the existing Radix Dialog primitive from /components/ui/dialog.jsx.
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

// ── Content ─────────────────────────────────────────────────────────────────

const PRIVACY_POLICY = `
## What This Covers

This policy explains what information VendBabee collects when you use our website, place an order, or contact us — and how we use it.

---

## What We Collect

**Information You Give Us**
- Name, email address, phone number
- Shipping and billing address
- Payment information (processed securely — we never store full card details)
- Messages sent through our contact forms or support channels

**Information Collected Automatically**
- Browser type and device info
- Pages visited and time spent on site
- IP address and general location data
- Cookies and similar tracking technologies

---

## How We Use It

We use your information to process and fulfill your order, send order confirmations and shipping updates, respond to questions and support requests, improve how the site works, and send relevant offers (you can opt out at any time).

We do not sell your personal data. We do not share it with third parties except where required to complete your order (e.g., shipping carriers, payment processors).

---

## Cookies

We use cookies to keep the site running smoothly and to understand how it's being used. You can disable cookies in your browser settings, but some features may not work as expected.

---

## Data Retention

We keep your order and account data as long as it's needed to service your purchase and meet legal requirements. You can request deletion of your data at any time.

---

## Your Rights

You have the right to access the personal data we hold about you, request corrections to inaccurate data, request deletion of your data, and opt out of marketing communications.

---

## Security

We use industry-standard measures to protect your data.

---

## Updates to This Policy

If this policy changes, we'll update the date at the top. Major changes will be communicated directly where required.

---

## Contact

Questions about your data? Email us at **info@vendbabee.com**
`;

const TERMS_AND_CONDITIONS = `
## Overview

These terms govern every order, interaction, and transaction with VendBabee. By placing an order or using this website, you agree to them in full.

---

## 1. Orders & Payment

All orders require full payment before processing begins. Once payment is confirmed, your unit moves into the fulfillment queue.

- We accept major credit/debit cards and approved payment methods listed at checkout
- Prices are listed in USD and are subject to change without notice
- Your order is confirmed only after successful payment

---

## 2. Shipping & Delivery

Delivery timelines are estimates. Actual transit times depend on your location, carrier availability, and logistics conditions outside our control.

- Shipping begins after full payment is cleared
- Once the unit leaves our facility, it's in the carrier's hands
- Risk of loss or damage transfers to the buyer upon handoff to the carrier
- We'll provide tracking information and support if issues arise during transit

If your shipment arrives damaged, document it at delivery and contact us within 48 hours.

---

## 3. Returns & Refunds

All orders are final once shipped. If there is a verified manufacturing defect, we'll handle it under the warranty terms below. If an order is cancelled before shipment, cancellation may be subject to a restocking or processing fee.

---

## 4. Warranty

You're covered for manufacturing defects during the stated warranty period. Coverage includes verified mechanical and electrical defects present at delivery, and replacement parts or units at our discretion.

Warranty does not cover damage caused by misuse, improper installation, unauthorized modification, normal wear and tear, or cosmetic damage reported after the delivery window.

To initiate a warranty claim, contact us with your order number and documentation (photos/video). Note: Warranties are fulfilled through the manufacturer. You may be required to contact the supplier directly as part of the claims process — we'll provide the relevant contact information when needed.

---

## 5. Installation & Setup

Installation is not included with your purchase. We provide setup documentation with every order. You are responsible for selecting a compliant installation location, ensuring local permits or compliance requirements are met, and safe and proper setup of the unit.

---

## 6. Product Descriptions & Accuracy

Minor variations in color, dimensions, or appearance may occur. These do not constitute grounds for return or refund.

---

## 7. Intellectual Property

All content on this website — including images, copy, logos, and design — is the property of VendBabee. You may not reproduce or use it without written permission.

---

## 8. Limitation of Liability

VendBabee is not liable for lost revenue, lost profits, or indirect damages arising from the use of our products, delays caused by carriers or third-party logistics providers, or issues resulting from improper installation or use. Our total liability in any dispute is limited to the amount paid for the order in question.

---

## 9. Governing Law

These terms are governed by the laws of the state in which VendBabee operates.

---

## 10. Changes to These Terms

We may update these terms at any time. Continued use of the site after updates means you accept the revised terms.

---

## Contact

Questions? Email us at **info@vendbabee.com**
`;

const FAQ_CONTENT = `
## Ordering

**Do I need to create an account to order?**
No. You can check out as a guest. An account makes it easier to track orders and reorder later.

**Can I change or cancel my order after placing it?**
If your order hasn't shipped yet, contact us immediately. Changes and cancellations may be possible, but are not guaranteed once processing begins. Cancellation may involve a fee.

**Is my payment information secure?**
Yes. Payments are processed through encrypted, PCI-compliant systems. We never store your full card details.

**Do you offer financing or payment plans?**
Not at this time. All orders require full payment at checkout.

---

## Shipping & Delivery

**When does shipping start?**
After full payment is completed. That's when your unit moves into processing and dispatch.

**How long does delivery take?**
Timelines vary by location and product. You'll get an estimated delivery window at checkout, plus tracking once your unit ships.

**Do you ship internationally?**
Contact us before ordering to confirm availability for your location.

**What if my order arrives damaged?**
Document the damage at delivery — photos and video. Contact us within 48 hours with your order number and documentation.

---

## Products

**What types of machines do you carry?**
VendBabee carries vending machines, arcade machines, and ticketing systems. Check the site for current stock.

**Are your machines new or refurbished?**
Product condition is listed on each listing. If you have questions about a specific unit, reach out before purchasing.

**Do the machines come ready to use?**
Most units are ready to operate once set up. Setup instructions are included with every order.

**Do you offer custom configurations or branding?**
Reach out to discuss options. Custom orders may have different lead times and pricing.

---

## Setup & Installation

**Is installation included?**
No. Installation isn't included. We send a full setup guide so you can get running quickly.

**Do I need an electrician or technician?**
Most units plug into a standard outlet. Check the spec sheet for your product.

**What if I need help during setup?**
Contact our support team. We'll walk you through it.

---

## Warranty & Support

**What does the warranty cover?**
You're covered for manufacturing defects during the warranty period. If something's wrong at delivery, we'll handle it.

**What's not covered?**
Damage from misuse, improper installation, unauthorized modifications, or normal wear and tear.

**How do I start a warranty claim?**
Email us with your order number, a description of the issue, and supporting photos or video. Note: warranty claims are processed through the manufacturer — we'll connect you with the right contact.

**How do I contact support?**
Email us at **info@vendbabee.com** — we respond quickly during business hours.

---

## Returns & Refunds

**Can I return my machine?**
All orders are final once shipped. If there's a verified defect, it's handled under warranty.

**What if I ordered the wrong product?**
Contact us before it ships. Once it leaves our facility, all sales are final.

---

## Running Your Machine

**How do I price products in my vending machine?**
That's up to you. Pricing flexibility is one of the advantages of owning your own unit.

**Where should I place my machine?**
High-traffic locations work best — offices, gyms, laundromats, waiting areas, apartment lobbies. You're responsible for securing placement and any required permits.

**How quickly can I expect to see returns?**
That depends on your location, product mix, and pricing. Most operators see consistent activity once the machine is well-placed.

---

Still have questions? Email **info@vendbabee.com** — we'll get back to you fast.
`;

const TERMS_OF_SALE = `
## Overview

These Terms of Sale apply to every product purchased through VendBabee. By completing a purchase, you agree to these terms in full. They work alongside our Terms & Conditions and apply specifically to the transaction itself.

---

## 1. Purchase Agreement

Your order becomes a binding purchase agreement once payment is confirmed and you receive an order confirmation from VendBabee. Confirmation is sent to the email address provided at checkout.

We reserve the right to cancel or refuse any order prior to fulfillment. If that happens, you'll be notified and any payment collected will be refunded in full.

---

## 2. Pricing

All prices are listed in USD. Prices are subject to change without notice and are only locked in at the time of confirmed purchase.

Taxes, duties, and applicable fees are calculated at checkout based on your location. These are the buyer's responsibility and are not included in the listed product price unless stated otherwise.

---

## 3. Payment Terms

Full payment is required to process any order. We do not hold inventory or reserve units without completed payment.

Accepted payment methods are displayed at checkout. VendBabee does not store full payment card details — all transactions are processed through encrypted, PCI-compliant payment systems.

If a payment is disputed or charged back without valid reason, VendBabee reserves the right to recover the full amount of the transaction including any associated fees.

---

## 4. Order Processing

Once payment clears, your order moves into processing and fulfillment. Processing times vary by product and availability. You'll receive shipping confirmation with tracking once your unit dispatches.

Note: Orders cannot be modified or cancelled once they've entered fulfillment.

---

## 5. Title & Risk of Loss

Title to purchased goods transfers to the buyer upon shipment. Risk of loss or damage transfers to the buyer at the point of handoff to the carrier.

If a unit arrives damaged, document the condition at delivery and contact us within 48 hours of receipt.

---

## 6. Sales Finality

All sales are final once an order has shipped. VendBabee does not accept returns on shipped orders outside of verified manufacturing defects handled under the applicable warranty.

If you believe you have received a defective unit, contact us at **info@vendbabee.com** with your order number and supporting documentation (photos/video).

---

## 7. Product Availability

Product listings are subject to availability. In the event that an ordered item becomes unavailable after purchase, VendBabee will notify you promptly and offer a refund or suitable alternative.

---

## 8. Resale

Products purchased through VendBabee may be resold. However, VendBabee is not responsible for any representations or warranties made by resellers to their customers. Support and warranty obligations remain between the original purchaser and VendBabee only.

---

## 9. Governing Terms

These Terms of Sale are governed by and construed in accordance with the laws of the state in which VendBabee operates. Any disputes arising from a transaction will be handled under applicable jurisdiction.

---

## Contact

Questions about your purchase? Email **info@vendbabee.com**
`;

const ABOUT_US = `
## What We Do

VendBabee supplies vending machines, arcade machines, and ticketing systems to operators across the United States.

We source reliable, commercial-grade equipment and make it straightforward to purchase, deploy, and run — whether you're setting up your first machine or expanding an existing operation.

The model is simple: you buy the equipment, place it in a high-traffic location, and it generates revenue on autopilot. We handle the sourcing and fulfillment side so you can focus on placement and scale.

---

## Who We Serve

Our customers are operators — people who see passive income as a system to build, not a gamble to take.

That includes first-time buyers getting into the vending business, established operators adding locations, and business owners looking to add a revenue stream to an existing space.

If you're serious about making your machines work, you're in the right place.

---

## What We Carry

- **Vending machines** — snack, beverage, combo, and specialty units built for consistent performance in commercial environments
- **Arcade machines** — single and multi-game cabinets suited for high-traffic entertainment venues, family spaces, and hospitality
- **Ticketing systems** — redemption and ticket-dispensing setups for arcades, family entertainment centers, and event venues

Inventory shifts regularly. Check the shop for what's currently available.

---

## How It Works

Order through the site. Pay in full at checkout. Your unit ships once payment clears.

Setup documentation ships with every order — most operators are up and running quickly. Installation isn't included, but everything you need to get started is.

Once it's live, your location starts generating. That's the setup.

---

## Our Approach

We don't oversell. We don't promise ROI numbers or make guarantees we can't back up.

What we do is make sure you have the right equipment, the right information, and a direct line to support if something comes up.

Operators typically recover their investment in months, not years — but results depend on placement, pricing, and product mix. We provide the tools. You run the operation.

---

## Get in Touch

Questions before you order? Want to discuss a larger purchase or B2B partnership?

Email us at **info@vendbabee.com** — we respond fast.
`;

const RETURN_POLICY = `
## The Short Version

All sales are final once an order has shipped. We don't accept returns on shipped units outside of verified manufacturing defects.

If there's a confirmed defect, it's handled under warranty — not as a return.

---

## Why We Don't Accept Returns

VendBabee sells commercial-grade equipment. These are large, purpose-built machines — not consumer products that can be easily restocked and resold.

Once a unit ships, it has left our facility, traveled through a carrier network, and arrived at your location. A change of mind or incorrect order selection is not grounds for a return or refund at that stage.

If you're not certain about a product before purchasing, contact us first. We'd rather answer your questions upfront than process a dispute after the fact.

---

## Pre-Shipment Cancellations

If your order hasn't shipped yet and you need to cancel, contact us immediately at **info@vendbabee.com**.

Cancellations made before fulfillment begins may be approved. A restocking or processing fee may apply. Once an order enters fulfillment, it cannot be cancelled.

---

## Damaged on Arrival

If your unit arrives visibly damaged, here's what to do:

- Document the damage before accepting or moving the unit — photos and video
- Note the damage on the carrier's delivery receipt if possible
- Contact us at **info@vendbabee.com** within **48 hours** of delivery with your order number and documentation

Damage reported outside this window may not be eligible for resolution. Do not delay.

---

## Defective Units

Manufacturing defects are covered under warranty. If your unit has a verified defect that was present at the time of delivery, we'll work to resolve it.

To start a claim, email **info@vendbabee.com** with your order number, a clear description of the issue, and photos or video demonstrating the defect.

Note: Warranty claims are fulfilled through the manufacturer. You may be required to contact the supplier directly — we'll provide the contact details when needed.

---

## What's Not Covered

The following are not eligible for return, replacement, or refund:

- Change of mind or buyer's remorse
- Incorrect product selection by the buyer
- Damage caused by misuse, improper installation, or unauthorized modification
- Normal wear and tear
- Cosmetic issues reported outside the 48-hour arrival window

---

## Questions

Not sure if your situation qualifies? Email us at **info@vendbabee.com** before filing a dispute. We'll give you a straight answer.
`;

const ACCESSIBILITY = `
## Our Commitment

VendBabee is committed to making our platform accessible to all users, including individuals with disabilities.

Our website, checkout experience, and customer-facing documents are designed to be usable regardless of how you access them. That includes product descriptions, pricing, and agreements — presented clearly so you can make informed decisions before you buy.

If something isn't working for you, contact us. We'll assist you directly.

---

## Standards We Aim to Meet

We strive to follow **WCAG 2.1 Level AA** guidelines across the VendBabee platform.

We have not completed a formal third-party accessibility audit. Accessibility is an ongoing effort — we continuously work toward improving conformance and treat it as part of how we build, not a separate task.

---

## What We've Implemented

We design and maintain our platform with accessibility in mind. Where possible, we implement:

- **Keyboard navigation** — core functions including navigation, product browsing, and checkout are designed to be operable via keyboard
- **Readable typography** — high-contrast, legible fonts at appropriate sizes across the site
- **Color contrast** — design choices that support readability for users with low vision
- **Structured content** — logical heading hierarchies to support screen readers and navigation tools
- **Alt text** — descriptive alternative text for product images where applicable
- **Visible focus indicators** — clear visual cues on all interactive elements
- **Responsive design** — layouts that adapt to different screen sizes and zoom levels without loss of content

---

## Known Limitations

We believe in being transparent about where gaps exist.

- **Supplier-provided materials** — product manuals, specification sheets, and technical documents are provided by third-party manufacturers. We do not control their structure or formatting, and they may not fully meet accessibility standards.
- **Third-party services** — payment processing, shipping tools, and other integrations are operated by external vendors. We do not control their accessibility, though we aim to work with providers that take it seriously.
- **Rich media content** — some visual or embedded content may have limited accessibility support depending on your browser or device.

We actively work to improve accessibility within the parts of the platform we control.

---

## Platform vs. Product Accessibility

This statement covers the VendBabee digital platform only.

Accessibility of physical products — vending machines, arcade equipment, kiosks, and related hardware — is determined by the manufacturer and falls outside the scope of this statement.

---

## How to Get Help

If you experience difficulty accessing any part of our website, checkout, or documents, reach out. We will assist you.

**Email:** info@vendbabee.com
**Response time:** 1–2 business days

We can provide:

- Plain-text or simplified versions of policies, agreements, or product information
- Manual assistance with checkout or order placement
- Direct clarification if any information is unclear or inaccessible

There is no wrong way to reach out. If something isn't working, we'll work with you to find a solution.

---

## Scope

This statement applies to:

- The VendBabee website (vendbabee.com)
- The checkout and order experience
- Customer-facing documents published by VendBabee (policies, agreements, FAQs)

It does not apply to third-party platforms, supplier-hosted content, or external services outside our control.

---

## Ongoing Improvement

Accessibility is not a one-time effort. We incorporate accessibility considerations into new features and updates, and conduct periodic internal reviews of the platform.

User feedback is the most useful signal we have. If you notice something that should be improved, let us know at **info@vendbabee.com**

---

## Formal Feedback & Escalation

If you've contacted us and your concern wasn't resolved to your satisfaction, you may seek support through relevant consumer protection or accessibility agencies in your region.

We take these concerns seriously and aim to resolve them directly — but we understand if you need to pursue other channels.
`;

const SHIPPING_AND_HANDLING = `
## How It Works

Orders are processed and dispatched after full payment is confirmed. Once your unit ships, you'll receive a tracking number by email so you can follow it to your door.

---

## Processing Time

Most orders begin processing within 1–3 business days of payment confirmation. Processing times can vary depending on product availability and order volume.

You'll be notified if there's a delay on our end.

---

## Delivery Timelines

Estimated delivery windows are provided at checkout. These are estimates — not guarantees. Actual transit times depend on your delivery location, the carrier's schedule and capacity, and external factors outside our control (weather, customs, logistics disruptions).

If your shipment is running behind the estimated window, check your tracking link first. If there's still a concern, contact us.

---

## Freight & Large Items

Vending machines, arcade units, and ticketing systems ship via freight carrier — not standard parcel services.

**Curbside delivery is standard.** The carrier brings the unit to the curb or loading area. Moving it inside is your responsibility.

**Liftgate service** is available for locations without a loading dock. If you need this, contact us before your order ships so we can arrange it.

**Delivery appointments** are typically scheduled by the freight carrier. Be available to receive the shipment during the agreed window.

---

## Risk of Loss

Once your unit leaves our facility and is handed off to the carrier, risk of loss or damage transfers to you. Inspect the shipment carefully at delivery.

If anything looks off — damaged packaging, visible dents, structural concerns — document it before the driver leaves. Photos and video are your best protection.

---

## Damaged Shipments

If your unit arrives damaged:

- Photograph and video the damage before moving the unit
- Note it on the carrier's delivery receipt
- Contact us at **info@vendbabee.com** within **48 hours** of delivery

Reports made outside this window may not be actionable. Don't wait.

---

## Domestic Shipping

VendBabee ships within the United States. Shipping costs are calculated at checkout based on your location and the product ordered.

---

## International Shipping

International orders are handled on a case-by-case basis. Contact us at **info@vendbabee.com** before placing an international order to confirm availability, rates, and timelines.

Import duties, customs fees, and applicable taxes are the buyer's responsibility.

---

## Order Tracking

Tracking information is sent to your email once your order ships. Use the tracking number to monitor your shipment directly with the carrier.

If you haven't received tracking details within your expected processing window, check your spam folder first — then contact us.

---

## Contact

Shipping question? Email **info@vendbabee.com** and include your order number.
`;

// ── Markdown renderer (lightweight, no deps) ────────────────────────────────

function renderMarkdown(md) {
  const lines = md.trim().split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={i}
          style={{
            fontFamily: "'Oswald', sans-serif",
            fontSize: "1rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "rgba(248,247,255,0.9)",
            marginTop: "1.6rem",
            marginBottom: "0.6rem",
          }}
        >
          {line.replace("## ", "")}
        </h2>
      );
    } else if (line === "---") {
      elements.push(
        <hr
          key={i}
          style={{
            border: "none",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            margin: "1.2rem 0",
          }}
        />
      );
    } else if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].replace("- ", ""));
        i++;
      }
      elements.push(
        <ul
          key={`ul-${i}`}
          style={{
            paddingLeft: "1.2rem",
            margin: "0.4rem 0 0.8rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.3rem",
          }}
        >
          {items.map((item, j) => (
            <li
              key={j}
              style={{ fontSize: "0.82rem", color: "rgba(248,247,255,0.7)", lineHeight: 1.55 }}
              dangerouslySetInnerHTML={{ __html: inlineFormat(item) }}
            />
          ))}
        </ul>
      );
      continue;
    } else if (line.startsWith("**") && line.endsWith("**") && !line.slice(2, -2).includes("**")) {
      elements.push(
        <p
          key={i}
          style={{
            fontSize: "0.84rem",
            fontWeight: 700,
            color: "rgba(248,247,255,0.9)",
            marginTop: "1rem",
            marginBottom: "0.25rem",
          }}
        >
          {line.slice(2, -2)}
        </p>
      );
    } else if (line.trim() === "") {
      // skip — spacing via margins
    } else {
      elements.push(
        <p
          key={i}
          style={{ fontSize: "0.82rem", color: "rgba(248,247,255,0.68)", lineHeight: 1.65, marginBottom: "0.3rem" }}
          dangerouslySetInnerHTML={{ __html: inlineFormat(line) }}
        />
      );
    }

    i++;
  }

  return elements;
}

function inlineFormat(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong style='color:rgba(248,247,255,0.9)'>$1</strong>")
    .replace(/`(.+?)`/g, "<code style='font-family:DM Mono,monospace;font-size:0.78rem;background:rgba(255,255,255,0.07);padding:1px 5px;border-radius:4px'>$1</code>");
}

// ── Modal configs ────────────────────────────────────────────────────────────

export const LEGAL_MODALS = {
  privacy: {
    title: "Privacy Policy",
    content: PRIVACY_POLICY,
    updated: "April 2026",
  },
  terms: {
    title: "Terms & Conditions",
    content: TERMS_AND_CONDITIONS,
    updated: "April 2026",
  },
  faq: {
    title: "Frequently Asked Questions",
    content: FAQ_CONTENT,
    updated: null,
  },
  termsOfSale: {
    title: "Terms of Sale",
    content: TERMS_OF_SALE,
    updated: "April 2026",
  },
  accessibility: {
    title: "Accessibility",
    content: ACCESSIBILITY,
    updated: "April 2026",
  },
  about: {
    title: "About Us",
    content: ABOUT_US,
    updated: null,
  },
  returns: {
    title: "Return Policy",
    content: RETURN_POLICY,
    updated: "April 2026",
  },
  shipping: {
    title: "Shipping & Handling",
    content: SHIPPING_AND_HANDLING,
    updated: "April 2026",
  },
};

// ── Component ────────────────────────────────────────────────────────────────

export default function LegalModal({ modalKey, open, onOpenChange }) {
  const config = LEGAL_MODALS[modalKey];
  if (!config) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-full"
        style={{
          background: "#0a0a0f",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 16,
          padding: 0,
          overflow: "hidden",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <DialogHeader
          style={{
            padding: "1.5rem 1.75rem 1rem",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            flexShrink: 0,
          }}
        >
          <DialogTitle
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontSize: "1.25rem",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              background: "linear-gradient(135deg, #5B8CFF, #A855F7)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {config.title}
          </DialogTitle>
          {config.updated && (
            <p style={{ fontSize: "0.7rem", color: "rgba(248,247,255,0.3)", marginTop: "0.25rem", fontFamily: "'DM Mono', monospace" }}>
              Last updated: {config.updated}
            </p>
          )}
        </DialogHeader>

        {/* Scrollable body */}
        <div
          style={{
            overflowY: "auto",
            padding: "1.25rem 1.75rem 2rem",
            flex: 1,
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(91,140,255,0.25) transparent",
          }}
        >
          {renderMarkdown(config.content)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
